import { useCallback } from 'react';
import { Transaction, VersionedTransaction } from '@solana/web3.js';
import { getSolanaNetwork } from '../utils/getSolanaNetwork';

export const useSecureRPC = () => {
  const network = getSolanaNetwork();

  const callRPC = useCallback(async (method: string, params: any[] = []) => {
    const response = await fetch('/api/solana-rpc', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-solana-network': network,
      },
      body: JSON.stringify({ method, params }),
    });

    if (!response.ok) {
      throw new Error(`RPC call failed: ${response.status}`);
    }

    const data = await response.json();
    
    if (data.error) {
      throw new Error(`RPC error: ${data.error.message}`);
    }

    return data.result;
  }, [network]);

  const simulateTransaction = useCallback(async (transaction: Transaction | VersionedTransaction) => {
    let serialized: string;
    let opts: Record<string, any>;
    if (transaction instanceof VersionedTransaction) {
      serialized = Buffer.from(transaction.serialize()).toString('base64');
      opts = {
        encoding: 'base64',
        sigVerify: false,
        replaceRecentBlockhash: true,
        commitment: 'processed',
      };
    } else {
      serialized = transaction.serialize({ requireAllSignatures: false, verifySignatures: false }).toString('base64');
      opts = {
        encoding: 'base64',
        sigVerify: false,
        commitment: 'processed',
      };
    }
    return callRPC('simulateTransaction', [serialized, opts]);
  }, [callRPC]);

  return {
    callRPC, // Expose callRPC for direct RPC method calls
    getBalance: (publicKey: string) => callRPC('getBalance', [publicKey]),
    getAccountInfo: (publicKey: string, commitment?: string) => 
      callRPC('getAccountInfo', [publicKey, { encoding: 'base64', commitment: commitment || 'confirmed' }]),
    getLatestBlockhash: async (commitment?: string) => {
      const resp = await callRPC('getLatestBlockhash', [{ commitment: commitment || 'processed' }]);
      // The RPC returns { context, value: { blockhash, lastValidBlockHeight } }
      return resp && resp.value ? resp.value : resp;
    },
    getSignatureStatuses: (signatures: string[]) =>
      callRPC('getSignatureStatuses', [signatures, { searchTransactionHistory: true }]),
    // NEW: relay raw transaction
    sendTransaction: (serializedTx: string, opts: any = {}) =>
      callRPC('sendTransaction', [serializedTx, opts]),
    // NEW: simulate transaction before signing
    simulateTransaction,
  };
};
